import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { ComboTicketsSelected } from '../../components/ComboTicketsList';
import { defaultBreadcrumbs, initialComboData } from '../../constants/combo';
import { CookiesKeys } from '../../constants/cookiesKeys';
import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { Combo, ComboDate, TicketGroupInCombo, TicketInCombo } from '../../types/ticket';
import getCookieValue from '../../utils/getCookieValue';
import { logger } from '../../utils/logger';
import { useApiService } from '../useApiService';
import { useCartContext } from './cart.context';
import { useEventContext } from './event.context';

type ComboBreadCrumbData = {
  name: string[];
  active: boolean;
  title: string[];
  description: string[];
  component: () => ReactNode;
};

export type ComboData = {
  id: number | null;
  name: string;
  comboDates: ComboDate[];
}

export type ComboProgressProps = {
  openToast: boolean;
  fetchingComboDetails: boolean;
  comboDetails: Combo;
  comboData: ComboData;
  breadcrumbs: ComboBreadCrumbData[];
  handleUpdateComboBreadcrumbs: (curActiveIndex: number) => void;
  selectCombo: (comboId: number, passkey?: string, breadcrumbsAlreadyFiltered?: ComboBreadCrumbData[] ) => void;
  selectSessions: (sessionsSelectedIds: string[]) => void;
  selectTickets: (ticketsSelected: ComboTicketsSelected[]) => void;
  handleCloseToast: () => void;
};

export type ResponseComboDetail = {
  responseData: { data: Combo };
  responseDetails: string;
  responseError: null;
  responseStatus: number;
};

const ComboProgressContext = createContext({} as ComboProgressProps);

function ComboProgressProvider({ children } : { children: ReactNode }) {
  const [breadcrumbs, setBreadcrumbs] = useState<ComboBreadCrumbData[]>([]);
  const [comboData, setComboData] = useState<ComboData>(initialComboData);
  const [comboDetails, setComboDetails] = useState<Combo>({} as Combo);
  const [fetchingComboDetails, setFetchingComboDetails] = useState(false);
  const [openToast, setOpenToast] = useState(false);

  const {
    comboList,
    event,
    passKeyApplied,
  } = useEventContext();
  const { request } = useApiService();
  const { updateComboInCart } = useCartContext();

  const handleUpdateComboBreadcrumbs = (
    plusInActiveIndex : number,
    breadcrumbsWithChanges? : ComboBreadCrumbData[]
  ) => {
    const scopedBreadcrumbs = breadcrumbsWithChanges || [...(breadcrumbs as ComboBreadCrumbData[])];
    const curActiveIndex = scopedBreadcrumbs.findIndex((breadcrumb) => breadcrumb.active);
    const newActiveIndex = curActiveIndex + plusInActiveIndex;

    const updatedBreadcrumbs = scopedBreadcrumbs.map((breadcrumb, index) => {
      if (index === newActiveIndex) return { ...breadcrumb, active : true };
      return { ...breadcrumb, active : false };
    });

    setBreadcrumbs(updatedBreadcrumbs);
  };

  const verifyNextStep = (
    selectedCombo: Combo | undefined,
    filteredBreadcrumbs: ComboBreadCrumbData[],
  ): ComboBreadCrumbData[] | undefined => {
    const alreadyRemovedLastStep = !filteredBreadcrumbs[filteredBreadcrumbs.length - 1].name.includes('STEP3');
    if (!selectedCombo || alreadyRemovedLastStep) return;
    const justOneTicketInSession = selectedCombo.comboDates.every((comboDate) => (
      comboDate.groups.every((group) => (
        group.tickets.length === 1
      ))
    ));

    const expectedBreadCrumbList = justOneTicketInSession ? (
      filteredBreadcrumbs.filter((_breadcrumb, index) => filteredBreadcrumbs.length - 1 !== index)
    ): filteredBreadcrumbs;

    return expectedBreadCrumbList;
  };

  const selectCombo = async (
    comboId: number,
    passkey = '',
    breadcrumbsAlreadyFiltered?: ComboBreadCrumbData[],
  ) => {
    const curPassKey = passKeyApplied || passkey;
    const comboDetail = await getComboDetails(comboId, curPassKey ? curPassKey : '');

    setComboData({
      id : comboId,
      name : String(comboDetail?.name),
      comboDates : [],
    });

    if (comboDetail) setComboDetails(comboDetail);
    let filteredBreadcrumbs = breadcrumbsAlreadyFiltered || breadcrumbs;
    filteredBreadcrumbs = verifyNextStep(comboDetail, filteredBreadcrumbs) || filteredBreadcrumbs;
    handleUpdateComboBreadcrumbs(1, filteredBreadcrumbs);
  };

  const getComboDetails = async (comboId: number, passkey = '') => {
    setFetchingComboDetails(true);
    try {
      const token = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const eventId = String(event?.id);

      const { responseData } = await request<null, ResponseComboDetail>(
        {
          requestOptions : {
            resourcePath : (
              /* eslint-disable max-len */
              `/event/${eventId}/combo/${comboId}/available?apikey=${Environment.API_KEY}&usertoken=${token}&passkey=${passkey}`
            ),
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL, needToken : true }
      );

      return responseData.data;
    } catch (error) {
      logger.error(error as string);
    } finally {
      setFetchingComboDetails(false);
    }
  };

  const handleCreateComboBreadcrumbs = (hasJustTwoSteps : boolean) => {

    if (hasJustTwoSteps) return defaultBreadcrumbs.slice(1, 3);
    return defaultBreadcrumbs;
  };

  const selectSessions = (sessionsSelectedIds : string[]) => {
    const comboDataList = comboDetails.comboDates.filter(({ id }) => (
      sessionsSelectedIds.includes(String(id))
    ));

    const lastStep = breadcrumbs.length - 1;
    const isLastStep = breadcrumbs[lastStep].active;

    const expectedComboData = {
      ...comboData,
      comboDates : comboDataList,
    };

    if (!isLastStep) {
      setComboData(expectedComboData);
      return handleUpdateComboBreadcrumbs(1);
    }

    const resetComboData = comboData.comboDates.length > 1;

    if (resetComboData) setComboData(initialComboData);

    updateComboInCart(expectedComboData);
    setBreadcrumbs((prev) => prev.map((breadcrumb, index) => {
      if (index === 0) return { ...breadcrumb, active : true };
      return { ...breadcrumb, active : false };
    }));

    setOpenToast(true);
    setTimeout(() => setOpenToast(false), 2500);
  };

  const selectTickets = (ticketsSelected : ComboTicketsSelected[]) => {
    const filterGroup = (group : TicketGroupInCombo, comboDate : ComboDate) => {
      return ticketsSelected.some((ticketSelected) => (
        ticketSelected[comboDate.id]?.groupId === group.ticketTypeId
      ));
    };

    const filterTicket = (ticket : TicketInCombo, comboDate : ComboDate) => {
      return ticketsSelected.some((ticketSelected) => (
        ticketSelected[comboDate.id]?.ticketId === ticket.id
      ));
    };

    const formattedComboDates = comboData.comboDates.map((comboDate) => ({
      ...comboDate,
      groups : comboDate.groups.filter((group) => filterGroup(group, comboDate))
        .map((group) => ({
          ...group,
          tickets : group.tickets.filter((ticket) => filterTicket(ticket, comboDate)),
        })),
    }));

    const expectedComboData = {
      ...comboData,
      comboDates : formattedComboDates,
    };

    updateComboInCart(expectedComboData);
    setBreadcrumbs((prev) => prev.map((breadcrumb, index) => {
      if (index === 0) return { ...breadcrumb, active : true };
      return { ...breadcrumb, active : false };
    }));
    setOpenToast(true);
    setTimeout(() => setOpenToast(false), 2500);
  };

  const handleCloseToast = () => setOpenToast(false);

  useEffect(() => {
    const hasComboList = comboList.length > 0;
    const passkey = passKeyApplied || '';
    if (hasComboList) {
      const hasJustTwoSteps = comboList.length === 1;
      const firstBreadcrumbsSetterValue = handleCreateComboBreadcrumbs(hasJustTwoSteps);
      if (hasJustTwoSteps) {
        selectCombo(comboList[0].id, passkey, firstBreadcrumbsSetterValue);
      } else {
        setComboDetails({} as Combo);
        handleUpdateComboBreadcrumbs(1, firstBreadcrumbsSetterValue);
      }
    }
  }, [comboList]);

  const shared = {
    openToast,
    fetchingComboDetails,
    comboDetails,
    breadcrumbs,
    comboData,
    handleUpdateComboBreadcrumbs,
    selectCombo,
    selectSessions,
    selectTickets,
    handleCloseToast,
  };

  return (
    <ComboProgressContext.Provider value={shared}>
      {children}
    </ComboProgressContext.Provider>
  );
}

ComboProgressProvider.displayName = 'ComboProgressProvider';
export const useComboProgressContext = (): ComboProgressProps => useContext(ComboProgressContext);

export { ComboProgressContext, ComboProgressProvider };
